import {
  extractQuickNoteEntities,
  normalizeQuickNoteReviewRows,
} from '../../../utils/quickNoteClassification';
import { groupRowsForReview } from '../../../utils/reviewRowGrouping';
import {
  CARE_QUICK_CAPTURE_STRONG_ACTIVITY_PATTERN,
  CARE_QUICK_CAPTURE_SUMMARY_SKIP_PATTERN,
  CARE_QUICK_CAPTURE_WEAK_ACTIVITY_PATTERNS,
} from '../../../shared/careSignalPatterns';

export const filterQuickNoteCaptureableEntities = (entities = []) => {
  if (!Array.isArray(entities) || !entities.length) {
    return [];
  }

  return entities.filter((entity) => {
    const normalized = String(entity?.text || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized) {
      return false;
    }

    if (CARE_QUICK_CAPTURE_SUMMARY_SKIP_PATTERN.test(normalized)) {
      return false;
    }

    if (entity?.category !== 'activity') {
      return true;
    }

    if (CARE_QUICK_CAPTURE_STRONG_ACTIVITY_PATTERN.test(normalized)) {
      return true;
    }

    return !CARE_QUICK_CAPTURE_WEAK_ACTIVITY_PATTERNS.some((pattern) => pattern.test(normalized));
  });
};

export const buildQuickNoteStructuredReviewRows = ({
  text = '',
  defaultDate = new Date(),
  childName = null,
  selectedDate = null,
  parserMode = 'quick_log',
  sourceMode = 'quick_log',
  inputMode = 'quick_log',
} = {}) => {
  const rawText = String(text || '').trim();
  if (!rawText) {
    return {
      rows: [],
      debug: {
        parserUsed: null,
        parserMode,
        sourceMode,
        inputMode,
        adapterMode: 'quick_log_structured',
        inputType: 'unsupported',
        reason: 'empty_text',
        rawTextLength: 0,
      },
    };
  }

  const extractedEntities = filterQuickNoteCaptureableEntities(extractQuickNoteEntities(rawText));
  if (!extractedEntities.length) {
    return {
      rows: [],
      debug: {
        parserUsed: 'extractQuickNoteEntities',
        parserMode,
        sourceMode,
        inputMode,
        adapterMode: 'quick_log_structured',
        inputType: 'review_needed',
        reason: 'no_structured_entities',
        rawTextLength: rawText.length,
        entityCount: 0,
      },
    };
  }

  const anchorDate = selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
    ? new Date(selectedDate)
    : (defaultDate instanceof Date ? new Date(defaultDate) : new Date());

  const groupedRows = groupRowsForReview(extractedEntities.map((entity, index) => {
    const rowDate = new Date(anchorDate);
    if (entity?.time) {
      const [hoursRaw, minutesRaw] = String(entity.time).split(':');
      const hours = Number(hoursRaw);
      const minutes = Number(minutesRaw);
      if (Number.isFinite(hours) && Number.isFinite(minutes)) {
        rowDate.setHours(hours, minutes, 0, 0);
      }
    }

    const entryText = String(entity?.text || '').trim();
    const entryCategory = String(entity?.category || 'log').trim() || 'log';

    return {
      id: `quick-note-${Date.now()}-${index}`,
      date: rowDate,
      note: entryText,
      body: entryText,
      text: entryText,
      content: entryText,
      description: entryText,
      summary: entryText,
      details: entryText,
      value: entryText,
      title: entity?.title || entryText,
      titlePrefix: entity?.titlePrefix || entity?.label || entryCategory,
      category: entryCategory,
      importance: 'normal',
      childName: childName || null,
      include: true,
      reviewConfidence: 'high',
      reviewConfidenceLabel: 'Looks right',
      reviewConfidenceReasons: [],
      timeSource: entity?.time ? 'explicit' : 'missing',
      localTime: entity?.time || null,
      timeBucket: entity?.timeBucket || null,
      timeInferred: false,
      timeInferenceReason: entity?.timeInferenceReason || '',
      metadata: {
        captureMode: 'small_structured_review',
        routingReason: extractedEntities.length > 1 ? 'multiple_local_events' : 'structured_event_signals',
        localConfidence: 'high',
        usedBackendExtraction: false,
      },
      sourceText: rawText,
      originalText: rawText,
      parsedObservation: entryCategory === 'mood' || entryCategory === 'sleep',
      parsedSourceCategory: entryCategory,
      parsedSourceType: entryCategory,
      parsedSourceConfidence: 'high',
    };
  }));

  const normalizedRows = normalizeQuickNoteReviewRows(groupedRows, {
    parserMode,
    sourceMode,
    inputMode,
    rawText,
    captureMode: 'small_structured_review',
    routingReason: extractedEntities.length > 1 ? 'multiple_local_events' : 'structured_event_signals',
    localConfidence: 'high',
    usedBackendExtraction: false,
  });

  return {
    rows: normalizedRows,
    debug: {
      parserUsed: 'extractQuickNoteEntities',
      parserMode,
      sourceMode,
      inputMode,
      adapterMode: 'quick_log_structured',
      inputType: 'small_structured_review',
      reason: extractedEntities.length > 1 ? 'multiple_local_events' : 'structured_event_signals',
      rawTextLength: rawText.length,
      entityCount: extractedEntities.length,
      rowCount: normalizedRows.length,
    },
  };
};

export const runQuickLogStructuredAdapter = (adapterInput = {}, {
  defaultDate = new Date(),
  childName = null,
  parserMode = 'quick_log',
  sourceMode = 'quick_log',
  inputMode = 'quick_log',
} = {}) => buildQuickNoteStructuredReviewRows({
  text: adapterInput.text,
  defaultDate,
  childName,
  selectedDate: adapterInput.selectedDate || defaultDate,
  parserMode,
  sourceMode,
  inputMode,
});
